import React, { useState, useEffect } from "react";
import { BiHomeAlt } from "react-icons/bi";
import { GoPlus } from "react-icons/go";
import { FiLoader } from "react-icons/fi";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeveloperActionsDropdown from "components/dropdown/DeveloperDropdown";
import DeveloperForm from "components/common/DeveloperForm";
import DeveloperViewModal from "components/modal/DeveloperViewModal";
import "styles/admin.css";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const DeveloperControl = ({ goBack }) => {
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDeveloper, setEditingDeveloper] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setViewOpen(true);

      const res = await api.get(`/api/admin/Developer/GetById/${id}`);
      const d = res.data?.data || res.data?.item || res.data;

      if (!d?.id) {
        toast.error("Dasturchi topilmadi");
        setViewOpen(false);
        return;
      }

      setSelectedDeveloper({
        id: d.id,
        fullName: d.fullName || "",
        position: d.position || "",
        phoneNumber: d.phoneNumber || "",
        bio: d.bio || "",
        gitUrl: d.gitUrl || "",
        instagramUrl: d.instagramUrl || "",
        linkedinUrl: d.linkedinUrl || "",
        telegramUsername: d.telegramUsername || "",
        imageUrl: d.imageUrl ? `${API_BASE}/${d.imageUrl}` : null,
      });
    } catch (err) {
      console.error("View error:", err);
      toast.error("Dasturchi ma'lumotlarini yuklab bo‘lmadi");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchDevelopers = async () => {
    try {
      setLoading(true);
      const res = await api.post("/api/admin/Developer/GetAll", {
        page: 1,
        pageSize: 100,
      });
      const list = res.data?.rows || res.data || [];
      setDevelopers(list);
      console.log(list);
    } catch (err) {
      console.error(err);
      toast.error("Dasturchilar yuklanmadi");
      setDevelopers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevelopers();
  }, []);

  const handleAdd = async (formData) => {
    const data = new FormData();
    data.append("FullName", formData.fullName.trim());
    data.append("Position", formData.position.trim());
    data.append("PhoneNumber", formData.phoneNumber.trim());
    if (formData.bio) data.append("Bio", formData.bio.trim());
    if (formData.gitUrl) data.append("GitUrl", formData.gitUrl.trim());
    if (formData.instagramUrl)
      data.append("InstagramUrl", formData.instagramUrl.trim());
    if (formData.linkedinUrl)
      data.append("LinkedinUrl", formData.linkedinUrl.trim());
    if (formData.telegramUsername)
      data.append("TelegramUsername", formData.telegramUsername.trim());
    if (!formData.file) return toast.error("Rasm majburiy!");
    data.append("File", formData.file);

    try {
      await api.post("/api/admin/Developer/Add", data);
      toast.success("Dasturchi qo‘shildi!");
      setShowForm(false);
      fetchDevelopers();
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : err.response?.data?.message || "Xato";
      toast.error("Xato: " + msg);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/Developer/GetById/${id}`);
      const d = res.data?.data || res.data?.item || res.data;

      setEditingDeveloper({
        id: d.id,
        fullName: d.fullName || "",
        position: d.position || "",
        phoneNumber: d.phoneNumber || "",
        bio: d.bio || "",
        gitUrl: d.gitUrl || "",
        instagramUrl: d.instagramUrl || "",
        linkedinUrl: d.linkedinUrl || "",
        telegramUsername: d.telegramUsername || "",
        imageUrl: d.imageUrl ? `${API_BASE}/${d.imageUrl}` : null,
      });
      setShowForm(true);
    } catch {
      toast.error("Ma'lumot yuklanmadi");
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingDeveloper?.id) return;

    const data = new FormData();
    data.append("FullName", formData.fullName.trim());
    data.append("Position", formData.position.trim());
    data.append("PhoneNumber", formData.phoneNumber.trim());
    if (formData.bio) data.append("Bio", formData.bio.trim());
    if (formData.gitUrl) data.append("GitUrl", formData.gitUrl.trim());
    if (formData.instagramUrl)
      data.append("InstagramUrl", formData.instagramUrl.trim());
    if (formData.linkedinUrl)
      data.append("LinkedinUrl", formData.linkedinUrl.trim());
    if (formData.telegramUsername)
      data.append("TelegramUsername", formData.telegramUsername.trim());
    if (formData.file && formData.file instanceof File)
      data.append("File", formData.file);

    try {
      await api.put(`/api/admin/Developer/Update/${editingDeveloper.id}`, data);
      toast.success("Dasturchi yangilandi!");
      setShowForm(false);
      setEditingDeveloper(null);
      fetchDevelopers();
    } catch {
      toast.error("Yangilashda xato");
    }
  };

  const handleSubmit = (formData) => {
    editingDeveloper ? handleUpdate(formData) : handleAdd(formData);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} theme="colored" />

      <div className="banner-page-wrapper">
        <motion.div
          className="banner-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div className="breadcrumb" whileHover={{ scale: 1.02 }}>
            <motion.div
              className="home-icon"
              whileHover={{ rotate: 360 }}
              onClick={goBack}
            >
              <BiHomeAlt />
            </motion.div>
            <span>/ Dasturchilar boshqaruvi</span>
          </motion.div>

          <motion.button
            className="add-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingDeveloper(null);
              setShowForm(true);
            }}
          >
            <GoPlus />
            Dasturchi qo‘shish
          </motion.button>
        </motion.div>

        {showForm ? (
          <DeveloperForm
            initialData={editingDeveloper}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingDeveloper(null);
            }}
            requireImage={!editingDeveloper}
          />
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "100px" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FiLoader size={50} />
            </motion.div>
            <p>Yuklanmoqda...</p>
          </div>
        ) : developers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "120px", color: "#666" }}>
            <h2>Hozircha dasturchi yo‘q</h2>
            <p>"Dasturchi qo‘shish" tugmasini bosing</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.table className="banner-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Rasm</th>
                  <th>Ism Familya</th>
                  <th>Lavozimi</th>
                  <th>Telefon</th>
                  <th>Status</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {developers.map((d, i) => (
                  <motion.tr
                    key={d.id}
                    layout
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <td>{i + 1}</td>
                    <td>
                      {d.imageUrl ? (
                        <motion.img
                          src={`${API_BASE}/${d.imageUrl}`}
                          alt={d.fullName || "Developer"}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: i * 0.1 }}
                          whileHover={{ scale: 1.15 }}
                          style={{
                            width: 70,
                            height: 70,
                            borderRadius: 14,
                            objectFit: "cover",
                            border: "3px solid #e0e7ff",
                            boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://via.placeholder.com/70x70/6366f1/ffffff?text=DEV";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 70,
                            height: 70,
                            borderRadius: 14,
                            background: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            fontSize: "12px",
                            fontWeight: "bold",
                            border: "2px dashed #cbd5e1",
                          }}
                        >
                          No Photo
                        </div>
                      )}
                    </td>
                    <td>{d.fullName}</td>
                    <td>{d.position}</td>
                    <td>{d.phoneNumber}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          d.canCancel ? "active" : "pending"
                        }`}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {d.canCancel ? "Faol" : "Kutilyapti"}
                      </span>
                    </td>
                    <td>
                      <DeveloperActionsDropdown
                        developer={d}
                        onEdit={() => handleEdit(d.id)}
                        refreshList={fetchDevelopers}
                        onView={() => handleView(d.id)}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </motion.table>
          </motion.div>
        )}
      </div>
      <DeveloperViewModal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedDeveloper(null);
        }}
        developer={selectedDeveloper}
        loading={viewLoading}
      />
    </>
  );
};

export default DeveloperControl;
